"use client";

import { DayButton, DayPicker, getDefaultClassNames } from "react-day-picker";

import { cn } from "../../utils";
import { Button, buttonVariants } from "../button";
import { IconChevronDown, IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { ComponentProps, useEffect, useRef } from "react";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = "label",
  buttonVariant = "ghost",
  formatters,
  components,
  ...props
}: ComponentProps<typeof DayPicker> & {
  buttonVariant?: ComponentProps<typeof Button>["variant"];
}) {
  const defaultClassNames = getDefaultClassNames();

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        "al-bg-background al-group/calendar al-p-3 [--cell-size:2rem] [[data-slot=card-content]_&]:al-bg-transparent [[data-slot=popover-content]_&]:al-bg-transparent",
        String.raw`rtl:**:[.rdp-button\_next>svg]:al-rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:al-rotate-180`,
        className
      )}
      captionLayout={captionLayout}
      formatters={{
        formatMonthDropdown: date => date.toLocaleString("default", { month: "short" }),
        ...formatters,
      }}
      classNames={{
        root: cn("al-w-fit", defaultClassNames.root),
        months: cn(
          "al-relative al-flex al-flex-col al-gap-4 md:al-flex-row",
          defaultClassNames.months
        ),
        month: cn("al-flex al-w-full al-flex-col al-gap-4", defaultClassNames.month),
        nav: cn(
          "al-absolute al-inset-x-0 al-top-0 al-flex al-w-full al-items-center al-justify-between al-gap-1",
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          "al-h-[--cell-size] al-w-[--cell-size] al-select-none al-p-0 aria-disabled:al-opacity-50",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          "al-h-[--cell-size] al-w-[--cell-size] al-select-none al-p-0 aria-disabled:al-opacity-50",
          defaultClassNames.button_next
        ),
        month_caption: cn(
          "al-flex al-h-[--cell-size] al-w-full al-items-center al-justify-center al-px-[--cell-size]",
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          "al-flex al-h-[--cell-size] al-w-full al-items-center al-justify-center al-gap-1.5 al-text-sm al-font-medium",
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          "has-focus:al-border-ring al-border-input al-shadow-xs has-focus:al-ring-ring/50 has-focus:al-ring-[3px] al-relative al-rounded-md al-border",
          defaultClassNames.dropdown_root
        ),
        dropdown: cn("al-absolute al-inset-0 al-opacity-0", defaultClassNames.dropdown),
        caption_label: cn(
          "al-select-none al-font-medium",
          captionLayout === "label"
            ? "al-text-sm"
            : "[&>svg]:al-text-muted-foreground al-flex al-h-8 al-items-center al-gap-1 al-rounded-md al-pl-2 al-pr-1 al-text-sm [&>svg]:al-size-3.5",
          defaultClassNames.caption_label
        ),
        table: "al-w-full al-border-collapse",
        weekdays: cn("al-flex", defaultClassNames.weekdays),
        weekday: cn(
          "al-text-muted-foreground al-flex-1 al-select-none al-rounded-md al-text-[0.8rem] al-font-normal",
          defaultClassNames.weekday
        ),
        week: cn("al-mt-2 al-flex al-w-full", defaultClassNames.week),
        week_number_header: cn(
          "al-w-[--cell-size] al-select-none",
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          "al-text-muted-foreground al-select-none al-text-[0.8rem]",
          defaultClassNames.week_number
        ),
        day: cn(
          "al-group/day al-relative al-aspect-square al-h-full al-w-full al-select-none al-p-0 al-text-center [&:first-child[data-selected=true]_button]:al-rounded-l-md [&:last-child[data-selected=true]_button]:al-rounded-r-md",
          defaultClassNames.day
        ),
        range_start: cn("al-bg-accent al-rounded-l-md", defaultClassNames.range_start),
        range_middle: cn("al-rounded-none", defaultClassNames.range_middle),
        range_end: cn("al-bg-accent al-rounded-r-md", defaultClassNames.range_end),
        today: cn(
          "al-bg-accent al-text-accent-foreground al-rounded-md data-[selected=true]:al-rounded-none",
          defaultClassNames.today
        ),
        outside: cn(
          "al-text-muted-foreground aria-selected:al-text-muted-foreground",
          defaultClassNames.outside
        ),
        disabled: cn("al-text-muted-foreground al-opacity-50", defaultClassNames.disabled),
        hidden: cn("al-invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return <div data-slot="calendar" ref={rootRef} className={cn(className)} {...props} />;
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === "left") {
            return <IconChevronLeft className={cn("al-size-4", className)} {...props} />;
          }

          if (orientation === "right") {
            return <IconChevronRight className={cn("al-size-4", className)} {...props} />;
          }

          return <IconChevronDown className={cn("al-size-4", className)} {...props} />;
        },
        DayButton: CalendarDayButton,
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="al-flex al-size-[--cell-size] al-items-center al-justify-center al-text-center">
                {children}
              </div>
            </td>
          );
        },
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  ...props
}: ComponentProps<typeof DayButton>) {
  const defaultClassNames = getDefaultClassNames();

  const ref = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      ref={ref}
      variant="ghost"
      size="icon"
      title={day.date.toLocaleDateString()}
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        "data-[selected-single=true]:al-bg-primary data-[selected-single=true]:al-text-primary-foreground data-[range-middle=true]:al-bg-accent data-[range-middle=true]:al-text-accent-foreground data-[range-start=true]:al-bg-primary data-[range-start=true]:al-text-primary-foreground data-[range-end=true]:al-bg-primary data-[range-end=true]:al-text-primary-foreground group-data-[focused=true]/day:al-border-ring group-data-[focused=true]/day:al-ring-ring/50 al-flex al-aspect-square al-h-auto al-w-full al-min-w-[--cell-size] al-flex-col al-gap-1 al-font-normal al-leading-none data-[range-end=true]:al-rounded-md data-[range-middle=true]:al-rounded-none data-[range-start=true]:al-rounded-md group-data-[focused=true]/day:al-relative group-data-[focused=true]/day:al-z-10 group-data-[focused=true]/day:al-ring-[3px] [&>span]:al-text-xs [&>span]:al-opacity-70",
        defaultClassNames.day,
        className
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
